import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React from 'react';
import { useSelector } from 'store';
import { WhatsappOriginalTemplate, WhatsappTemplateHeaderFormat } from 'types/templates';
import LinkifiedText from 'ui-component/system/LinkifiedText';
import { safeParseJSON } from 'utils/helpers';
import ImageMessage from '../media/ImageMessage';
import { MediaPreviewType, MessageListRefProps } from 'types/chat/props';
import DocMessage from '../media/DocMessage';
import UnderMessageButton from '../UnderMessageButton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import Portal from 'ui-component/system/Portal';
import VideoMessage from '../media/VideoMessage';
import { MessageStatus } from 'types/chat/messages/common';

interface TemplateMessageProps {
  text?: string | null;
  isIncoming: boolean;
  handleMediaPreview: (params: Partial<MediaPreviewType>) => void;
  buttonsContainerRef: React.RefObject<HTMLDivElement>;
  status: MessageStatus | null | undefined;
  scrollToBottom: MessageListRefProps['scrollToBottom'];
  mediaShouldScrollBottomRef: React.MutableRefObject<boolean>;
}

const TemplateMessage: React.FC<TemplateMessageProps> = ({
  text,
  isIncoming,
  handleMediaPreview,
  buttonsContainerRef,
  status,
  scrollToBottom,
  mediaShouldScrollBottomRef,
}) => {
  const { whatsappOriginalTemplates } = useSelector((state) => state.templates);
  const chatCustomizationSettings = useSelector((state) => state.companyPlatforms.chatCustomizationSettings);

  const { doc, isDocParsed, templateFileType } = React.useMemo(() => {
    let doc = safeParseJSON(text);
    let isDocParsed = false;
    let templateFileType: WhatsappTemplateHeaderFormat | null = null;

    if (doc?.components) {
      const currentTemplateInList = whatsappOriginalTemplates.find((item) => item.name === doc.name);

      if (currentTemplateInList) {
        const paramsObject: WhatsappOriginalTemplate = JSON.parse(JSON.stringify(currentTemplateInList));

        let newDoc: any = {};
        paramsObject.components.forEach((comp) => {
          if (comp.type === 'BUTTONS') {
            newDoc['BUTTONS'] = {
              buttons: comp.buttons,
            };
            if (doc.components.some((item: any) => item.type === 'button')) {
              const docBtnComp = doc.components.find((item: any) => item.type === 'button');
              const urlBtn = newDoc['BUTTONS'].buttons.find((btn: any) => btn.type === 'URL');
              urlBtn.url = urlBtn.url.replace(`{{1}}`, docBtnComp.parameters[0]?.text);
            }
          } else if (comp.type === 'HEADER') {
            newDoc['HEADER'] = {
              format: comp.format,
            };

            if (doc.components.some((item: any) => item.type === 'HEADER')) {
              const params = doc.components.find((item: any) => item.type === 'HEADER').parameters;

              if (comp.format === 'LOCATION') {
              } else if (comp.format === 'DOCUMENT') {
                newDoc['HEADER'].text = params[0].document.link;
              } else if (comp.format === 'IMAGE') {
                newDoc['HEADER'].text = params[0].image.link;
              } else if (comp.format === 'VIDEO') {
                newDoc['HEADER'].text = params[0].video.link;
              } else {
                newDoc['HEADER'].text = comp.text;
                params.forEach((param: any, index: number) => {
                  newDoc['HEADER'].text = newDoc[comp.type].text.replace(`{{${index + 1}}}`, param.text);
                });
              }
            } else {
              newDoc['HEADER'].text = comp.text;
            }
          } else {
            newDoc[comp.type] = {
              text: comp.text,
            };

            if (doc.components.some((item: any) => item.type === comp.type)) {
              const params = doc.components.find((item: any) => item.type === comp.type).parameters;

              params.forEach((param: any, index: number) => {
                newDoc[comp.type].text = newDoc[comp.type].text.replace(`{{${index + 1}}}`, param.text);
              });
            }
          }
        });

        doc = newDoc;
        isDocParsed = true;
      }
    } else if (doc?.['BODY']) {
      isDocParsed = true;
    }

    templateFileType = doc?.['HEADER']?.format;

    return { doc, isDocParsed, templateFileType };
  }, [text, whatsappOriginalTemplates]);

  const buttonIconByType = {
    URL: OpenInNewIcon,
    QUICK_REPLY: undefined,
    PHONE_NUMBER: PhoneIcon,
  };

  return (
    <React.Fragment>
      {doc && typeof doc === 'object' ? (
        isDocParsed ? (
          <Stack direction="column" spacing={1}>
            {doc['HEADER'] && (
              <Box>
                {doc['HEADER'].format === 'TEXT' ? (
                  <Typography
                    sx={{
                      color:
                        chatCustomizationSettings?.find(
                          (option) => option.type === (!isIncoming ? 'outcomingMessage' : 'incomingMessage'),
                        )?.value === 'custom'
                          ? chatCustomizationSettings?.find(
                              (option) =>
                                option.type === (!isIncoming ? 'outcomingMessage' : 'incomingMessage'),
                            )?.customColor.text + ' !important'
                          : null,
                      fontWeight: 700,
                      letterSpacing: 0.17,
                    }}
                  >
                    {doc['HEADER'].text}
                  </Typography>
                ) : (
                  <React.Fragment>
                    {templateFileType === 'IMAGE' && (
                      <ImageMessage
                        file={doc['HEADER'].text}
                        fileType="image"
                        mediaNoteFormat={null}
                        handleMediaPreview={handleMediaPreview}
                        status={status}
                        scrollToBottom={scrollToBottom}
                        mediaShouldScrollBottomRef={mediaShouldScrollBottomRef}
                      />
                    )}
                    {templateFileType === 'VIDEO' && (
                      <VideoMessage
                        file={doc['HEADER'].text}
                        status={status}
                        scrollToBottom={scrollToBottom}
                        mediaShouldScrollBottomRef={mediaShouldScrollBottomRef}
                      />
                    )}
                    {templateFileType === 'DOCUMENT' && (
                      <DocMessage
                        file={doc['HEADER'].text}
                        fileType="document"
                        mediaNoteFormat={null}
                        object={doc['HEADER'].text}
                        status={status}
                        handleMediaPreview={handleMediaPreview}
                      />
                    )}
                  </React.Fragment>
                )}
                {
                  //TODO ) : doc['HEADER'].format === 'LOCATION' ? (
                  //   <p>
                  //     {doc['HEADER'].location?.latitude} {doc['HEADER'].location?.longitude}
                  //     <br />
                  //     {doc['HEADER'].location?.name}
                  //     <br />
                  //     {doc['HEADER'].location?.address}
                  //   </p>
                  // )
                  // :
                }
              </Box>
            )}
            <Typography
              sx={{
                color:
                  chatCustomizationSettings?.find(
                    (option) => option.type === (isIncoming ? 'outcomingMessage' : 'incomingMessage'),
                  )?.value === 'custom'
                    ? chatCustomizationSettings?.find(
                        (option) => option.type === (isIncoming ? 'outcomingMessage' : 'incomingMessage'),
                      )?.customColor.text + ' !important'
                    : null,
                whiteSpace: 'pre-line',
                wordWrap: 'break-word',
              }}
              className="template-text"
            >
              {doc['BODY']?.text && (
                <LinkifiedText
                  text={doc['BODY']?.text || ''}
                  websitePreviewObj={null}
                  messageType={undefined}
                />
              )}
            </Typography>
            {doc['FOOTER'] && (
              <Grid size={12}>
                <Typography fontSize={12} color="text.secondary">
                  {doc['FOOTER'].text}
                </Typography>
              </Grid>
            )}
            {doc['BUTTONS'] && (
              <Portal containerRef={buttonsContainerRef}>
                <Stack direction={'column'} width="100%">
                  {doc['BUTTONS']?.buttons?.map((button: any, index: number) => (
                    <UnderMessageButton
                      key={`${button.type.toLowerCase()}-${button.text}`}
                      text={button.text}
                      icon={buttonIconByType[button.type as keyof typeof buttonIconByType]}
                      type={button.type === 'URL' ? 'link' : 'button'}
                      href={button.url}
                      showDivider={Boolean(
                        doc['BUTTONS'].buttons.length > 0 && index !== doc['BUTTONS'].buttons.length - 1,
                      )}
                    />
                  ))}
                </Stack>
              </Portal>
            )}
          </Stack>
        ) : (
          <Typography
            sx={{
              color:
                chatCustomizationSettings?.find(
                  (option) => option.type === (!isIncoming ? 'outcomingMessage' : 'incomingMessage'),
                )?.value === 'custom'
                  ? chatCustomizationSettings?.find(
                      (option) => option.type === (!isIncoming ? 'outcomingMessage' : 'incomingMessage'),
                    )?.customColor.text + ' !important'
                  : null,
            }}
          >
            <LinkifiedText text={doc.text || ''} websitePreviewObj={null} messageType={undefined} />
          </Typography>
        )
      ) : (
        <Typography
          sx={{
            color:
              chatCustomizationSettings?.find(
                (option) => option.type === (!isIncoming ? 'outcomingMessage' : 'incomingMessage'),
              )?.value === 'custom'
                ? chatCustomizationSettings?.find(
                    (option) => option.type === (!isIncoming ? 'outcomingMessage' : 'incomingMessage'),
                  )?.customColor.text + ' !important'
                : null,
          }}
        >
          {text ? text : ''}
        </Typography>
      )}
    </React.Fragment>
  );
};

export default TemplateMessage;
